import { EDatasourceType, IActionDefinitionDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import { canManageActions, deleteActionFromRow, hasActionRowMenu } from "../datasource-row-actions";
import { getDatasourceDisplayName } from "../datasources-landing-model";
import { getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { buildActionSwitcherItemsOfType } from "./editor-switcher-items";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * The identity chip on the Blockly editor's bar: which action is open, and the way to the
 * other Blockly actions of the same datasource.
 *
 * A pick is PARKED as the editor's pending close rather than opened outright, so the
 * editor's guard gets to offer a save first.
 */

export interface IBlocklyActionSwitcherChipProps {
  sd: IScreenDesignContextState;
  setNextCallbackFunction: (callback?: () => void) => void;
  datasource: IDatasourceDto | undefined;
  currentActionId: string | undefined;
  currentName: string;
  /** The editor's guarded close — it leads to the datasources list. */
  onManageAll: () => void;
}

export const BlocklyActionSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  datasource,
  currentActionId,
  currentName,
  onManageAll
}: IBlocklyActionSwitcherChipProps): React.ReactElement => {
  const { deleteOption, deleteModal }: ISwitcherDelete<IActionDefinitionDto> = useSwitcherDelete(
    "action",
    (action: IActionDefinitionDto): void => void deleteActionFromRow(sd, action)
  );
  // The datasources list's own rule for an action row's Delete.
  const canDeleteAction: boolean = canManageActions(sd) && !!datasource && hasActionRowMenu(datasource);
  const glyph: IEditorGlyph = getEditorGlyph({
    kind: "action",
    actionKind: "blockly",
    datasourceName: datasource ? getDatasourceDisplayName(datasource) : undefined
  });

  const items: KuikaEntitySwitcherItem[] = buildActionSwitcherItemsOfType(
    sd.actionDefinitions ?? [],
    datasource?.id,
    EDatasourceType.Blockly
  );

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={currentName}
        triggerTestId="blocklyActionSwitcherTrigger"
        items={items}
        current={currentActionId ? { id: currentActionId, name: currentName } : undefined}
        entityLabel="Blockly Action"
        iconName="puzzle"
        iconType="datasource-icons"
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const action: IActionDefinitionDto | undefined = (sd.actionDefinitions ?? []).find(
            (candidate: IActionDefinitionDto): boolean => candidate.id === item.id
          );
          if (!action || !datasource) return;
          setNextCallbackFunction((): void => sd.setEditingActionsDatasource(datasource, action, false, false));
        }}
        onCreateNew={
          canManageActions(sd)
            ? (): void => {
                if (!datasource) return;
                setNextCallbackFunction((): void =>
                  sd.setEditingActionsDatasource(
                    datasource,
                    undefined,
                    true,
                    false,
                    undefined,
                    // The same unique name the left tree generates, so two routes to "new" do not
                    // disagree about what the action is called.
                    KMainFunctions.defaultUniqueListElementNameGenerator(
                      sd.actionDefinitions ?? [],
                      "name",
                      "Blockly_Action"
                    )
                  )
                );
              }
            : undefined
        }
        onManageAll={onManageAll}
        getRowActions={(item: KuikaEntitySwitcherItem) => {
          const action: IActionDefinitionDto | undefined = (sd.actionDefinitions ?? []).find(
            (candidate: IActionDefinitionDto): boolean => candidate.id === item.id
          );
          return action ? [deleteOption(action, !canDeleteAction)] : [];
        }}
      />
      {deleteModal}
    </>
  );
};
